import * as React from 'react';

/**
 * Fraction of the viewport a panel may occupy when no explicit `maxWidth` is
 * given. Capped well below 1 so the content the panel overlays — and any drop
 * targets living in it — stays reachable at any width.
 */
const DEFAULT_MAX_WIDTH_RATIO = 0.6;

/** Pixels added/removed per arrow-key press, and per Shift + arrow press. */
const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_LARGE = 64;

/**
 * Distance in px within which a live drag snaps exactly onto `minWidth`.
 * Gives the "docked" width a tactile detent — similar to OS window-edge
 * snapping — so a user dragging back toward it lands precisely on it instead
 * of hovering a pixel or two off. Keyboard resizing (fixed 16/64px steps,
 * `Home` for the exact floor) is already precise and isn't snapped.
 */
const SNAP_TO_MIN_WIDTH_THRESHOLD = 6;

function readStoredWidth(storageKey?: string): number | null {
  if (!storageKey || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Storage can throw in private mode or when disabled by policy. A missing
    // preference just means we fall back to the default width.
    return null;
  }
}

function writeStoredWidth(storageKey: string | undefined, width: number) {
  if (!storageKey || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, String(Math.round(width)));
  } catch {
    // Persistence is best-effort — a quota or policy failure must not break
    // the resize interaction itself.
  }
}

interface UseResizablePanelOptions {
  /**
   * Width in px used when nothing has been persisted yet.
   * @required
   */
  defaultWidth: number;
  /**
   * Smallest width in px the panel can be dragged to.
   * @required
   */
  minWidth: number;
  /**
   * Largest width in px. When omitted, the cap is derived from the viewport
   * via `maxWidthRatio`. When both are set, the smaller of the two wins.
   * @optional
   */
  maxWidth?: number;
  /**
   * Fraction of the viewport width the panel may occupy.
   * @optional
   * @default 0.6
   */
  maxWidthRatio?: number;
  /**
   * `localStorage` key the chosen width is persisted under. Omit to keep the
   * width in memory for the lifetime of the component only.
   * @optional
   */
  storageKey?: string;
  /**
   * Edge of the screen the panel is anchored to. Determines which drag
   * direction grows it: a `right` panel grows as the pointer moves left.
   * @optional
   * @default 'right'
   */
  side?: 'left' | 'right';
  /**
   * Whether the panel is resizable at all. When `false` the hook still returns
   * a coherent result, but subscribes to nothing and persists nothing — so a
   * consumer that renders both resizable and non-resizable panels can call it
   * unconditionally (as the Rules of Hooks require) without every non-resizable
   * instance holding a `resize` listener and a storage write for the life of
   * the page.
   * @optional
   * @default true
   */
  isEnabled?: boolean;
}

interface UseResizablePanelResult {
  /** Current width in px, already clamped to the min/max bounds. */
  width: number;
  /** True while a pointer drag is in flight. */
  isResizing: boolean;
  /** Resolved upper bound in px for the current viewport. */
  maxWidth: number;
  /**
   * Props for the drag handle element. Spread onto a focusable element that
   * sits on the panel's resizable edge.
   */
  separatorProps: {
    role: 'separator';
    tabIndex: 0;
    'aria-orientation': 'vertical';
    'aria-valuenow': number;
    'aria-valuemin': number;
    'aria-valuemax': number;
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onDoubleClick: () => void;
  };
  /** Restores `defaultWidth`. Also bound to double-click and Enter. */
  reset: () => void;
}

/**
 * Drives a pointer- and keyboard-resizable panel, persisting the chosen width.
 *
 * The width is always clamped to `[minWidth, maxWidth]`, and re-clamped when
 * the viewport changes — so a width stored on a wide monitor shrinks to fit
 * when the same user opens the page on a laptop rather than overflowing it.
 *
 * Follows the ARIA window-splitter pattern: the handle is a focusable
 * `separator` whose value is the panel width, so it is operable by keyboard
 * (arrows to resize, Shift for larger steps, Home/End for the bounds, Enter to
 * reset) and not pointer-only.
 *
 * @param options - Sizing bounds, persistence key, and anchored side
 * @returns The current width, drag state, and props for the handle element
 *
 * @example
 * const { width, separatorProps, isResizing } = useResizablePanel({
 *   defaultWidth: 320,
 *   minWidth: 240,
 *   storageKey: 'my-panel-width',
 * });
 *
 * <aside style={{ width }}>
 *   <div {...separatorProps} aria-label="Resize panel" className="cursor-col-resize" />
 * </aside>
 */
function useResizablePanel(options: UseResizablePanelOptions): UseResizablePanelResult {
  const {
    defaultWidth,
    minWidth,
    maxWidth: maxWidthProp,
    maxWidthRatio = DEFAULT_MAX_WIDTH_RATIO,
    storageKey,
    side = 'right',
    isEnabled = true,
  } = options;

  const resolveMaxWidth = React.useCallback(() => {
    const viewportMax = typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth * maxWidthRatio;
    // `minWidth` wins if the viewport is so narrow that the ratio would push
    // the cap below it, keeping the range non-inverted.
    return Math.max(minWidth, Math.min(maxWidthProp ?? Number.POSITIVE_INFINITY, viewportMax));
  }, [maxWidthProp, maxWidthRatio, minWidth]);

  const clampWidth = React.useCallback(
    (next: number) => Math.round(Math.min(Math.max(next, minWidth), resolveMaxWidth())),
    [minWidth, resolveMaxWidth],
  );

  // Clamped up front, not just by the effect below: a stored width from a wider
  // monitor would otherwise be reported — and rendered, and published as
  // `aria-valuenow` — out of bounds for one paint before the effect corrects it.
  const [width, setWidth] = React.useState(() => clampWidth(readStoredWidth(storageKey) ?? defaultWidth));
  const [maxWidth, setMaxWidth] = React.useState(() => resolveMaxWidth());
  const [isResizing, setIsResizing] = React.useState(false);

  const dragRef = React.useRef<{ startX: number; startWidth: number } | null>(null);
  const frameRef = React.useRef<number | null>(null);

  // Re-clamp on mount (a persisted width may not fit this viewport) and on
  // every viewport change, so the panel stays responsive rather than pinned to
  // a width chosen on a different screen.
  React.useEffect(() => {
    if (!isEnabled) return;

    const handleViewportResize = () => {
      setMaxWidth(resolveMaxWidth());
      setWidth((current) => clampWidth(current));
    };

    handleViewportResize();
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, [clampWidth, isEnabled, resolveMaxWidth]);

  // Persist only once the drag settles — writing on every pointermove would
  // hammer localStorage synchronously and jank the drag.
  React.useEffect(() => {
    if (!isEnabled || isResizing) return;
    writeStoredWidth(storageKey, width);
  }, [isEnabled, isResizing, storageKey, width]);

  React.useEffect(() => {
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  // A drag can outlive the handle. `handlePointerEnd` is bound to the handle
  // itself, so anything that unmounts it mid-gesture — closing the drawer,
  // dropping below the breakpoint that hides it — takes the only thing that
  // would clear `isResizing` with it, and the panel stays stuck reporting a
  // drag that ended. Listening on the window terminates the gesture wherever
  // it finishes: on the handle, off it, or with the handle already gone.
  //
  // Ordering is safe against the handle's own listener. React delegates from
  // the root container, well inside the window, so on a normal release
  // `handlePointerEnd` runs first and releases pointer capture; this then
  // re-clears state that is already clear.
  React.useEffect(() => {
    if (!isResizing) return;

    const endDrag = () => {
      dragRef.current = null;
      setIsResizing(false);
    };

    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [isResizing]);

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      // Ignore secondary buttons so a right-click on the handle doesn't start a
      // drag the user can't see themselves having begun.
      if (event.button !== 0) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { startX: event.clientX, startWidth: width };
      setIsResizing(true);
    },
    [width],
  );

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag) return;

      const delta = event.clientX - drag.startX;
      // A right-anchored panel grows as the pointer travels left, so the delta
      // is inverted relative to a left-anchored one.
      const rawNextWidth = drag.startWidth + (side === 'right' ? -delta : delta);
      // Pull the drag onto the docked baseline when it's within a few px of
      // it, so the panel's in-flow/no-shadow "docked" state is easy to land
      // on exactly rather than requiring pixel-perfect precision.
      const nextWidth = Math.abs(rawNextWidth - minWidth) <= SNAP_TO_MIN_WIDTH_THRESHOLD ? minWidth : rawNextWidth;

      // Coalesce to one state update per frame; pointermove can fire far more
      // often than the browser paints.
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        setWidth(clampWidth(nextWidth));
      });
    },
    [clampWidth, minWidth, side],
  );

  const handlePointerEnd = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;

    dragRef.current = null;
    setIsResizing(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const reset = React.useCallback(() => setWidth(clampWidth(defaultWidth)), [clampWidth, defaultWidth]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;
      // Grow/shrink keys mirror the drag direction for each anchored side.
      const growKey = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
      const shrinkKey = side === 'right' ? 'ArrowRight' : 'ArrowLeft';

      switch (event.key) {
        case growKey:
          event.preventDefault();
          setWidth((current) => clampWidth(current + step));
          break;
        case shrinkKey:
          event.preventDefault();
          setWidth((current) => clampWidth(current - step));
          break;
        case 'Home':
          event.preventDefault();
          setWidth(clampWidth(minWidth));
          break;
        case 'End':
          event.preventDefault();
          setWidth(clampWidth(Number.POSITIVE_INFINITY));
          break;
        case 'Enter':
          event.preventDefault();
          reset();
          break;
        default:
          break;
      }
    },
    [clampWidth, minWidth, reset, side],
  );

  return {
    width,
    isResizing,
    maxWidth,
    reset,
    separatorProps: {
      role: 'separator',
      tabIndex: 0,
      'aria-orientation': 'vertical',
      'aria-valuenow': width,
      'aria-valuemin': minWidth,
      'aria-valuemax': Math.round(maxWidth),
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onKeyDown: handleKeyDown,
      onDoubleClick: reset,
    },
  };
}

export { useResizablePanel };
export type { UseResizablePanelOptions, UseResizablePanelResult };
